import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import {
  BellOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  CloseOutlined,
  DesktopOutlined,
  DisconnectOutlined,
  EyeOutlined,
  FieldTimeOutlined,
  LaptopOutlined,
} from "@ant-design/icons";
import apiClient from "../../actions/api";
import openNotification from "../../components/Notifications/openNotification";
import isEmpty from "../../validations/is-empty";
import "../../style/screenshots/screenshots.scss";

const SOCKET_BASE_URL = process.env.REACT_APP_SOCKET_BASE_URL;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Screenshots = ({ title }) => {
  document.title = title;
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [userList, setUserList] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const loader = useRef(null);
  const socket = useRef();
  const selectedUserRef = useRef(null);
  const audioEnabledRef = useRef(false);

  useEffect(() => {
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);
  useEffect(() => {
    audioEnabledRef.current = audioEnabled;
  }, [audioEnabled]);

  const playBeep = () => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(600, ctx.currentTime);
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.2);
  };

  const fetchPosts = async (pageNumber, pcName) => {
    if (!pcName) return;
    setLoading(true);
    try {
      const res = await apiClient.post("/screenshots/get", {
        page: pageNumber,
        limit: 24,
        pcName,
      });
      setPosts((prev) =>
        pageNumber === 1 ? res.data.data : [...prev, ...res.data.data],
      );
      setHasMore(pageNumber < res.data.totalPages);
      setPage(pageNumber + 1);
    } catch (err) {
      if (!isEmpty(err?.response?.data?.errors)) {
        err.response.data.errors.forEach((item) =>
          openNotification("error", "Validation Error!", item.msg),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedUser) return;
    setPosts([]);
    setPage(1);
    setHasMore(true);
    fetchPosts(1, selectedUser);
  }, [selectedUser]);

  useEffect(() => {
    apiClient
      .get("/screenshots/pcs")
      .then((res) => setUserList(res.data))
      .catch(() => {});
    socket.current = io(SOCKET_BASE_URL);
    socket.current.emit("admin-join");
    socket.current.on("user-list-initial", setUserList);
    socket.current.on("user-list-update", (user) =>
      setUserList((prev) => {
        const index = prev.findIndex((item) => item.pcName === user.pcName);
        if (index < 0) return [...prev, user];
        const updated = [...prev];
        updated[index] = user;
        return updated;
      }),
    );
    socket.current.on("user-presence", (user) =>
      setUserList((prev) =>
        prev.map((item) => (item.pcName === user.pcName ? user : item)),
      ),
    );
    socket.current.on("user-disconnected", (pcName) =>
      setUserList((prev) =>
        prev.map((user) => ({
          ...user,
          socketId: user.pcName === pcName ? "" : user.socketId,
        })),
      ),
    );
    socket.current.on("capture-state-update", ({ pcName, captureEnabled }) =>
      setUserList((prev) =>
        prev.map((user) =>
          user.pcName === pcName ? { ...user, captureEnabled } : user,
        ),
      ),
    );
    socket.current.on("send-screen-data", (res) => {
      if (res.pcName !== selectedUserRef.current) return;
      setPosts((prev) => [res, ...prev]);
      if (audioEnabledRef.current) playBeep();
    });
    const handleActivity = (res) => {
      if (res.pcName === selectedUserRef.current)
        setPosts((prev) => [res, ...prev]);
    };
    socket.current.on("send-activity-data", handleActivity);
    socket.current.on("send-key-data", handleActivity);
    return () => socket.current?.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loading && hasMore)
          fetchPosts(page, selectedUser);
      },
      { threshold: 1 },
    );
    const currentLoader = loader.current;
    if (currentLoader) observer.observe(currentLoader);
    return () => {
      if (currentLoader) observer.unobserve(currentLoader);
    };
  }, [page, hasMore, loading, selectedUser]);

  const selectUser = (pcName) => {
    setSelectedUser(pcName);
    socket.current?.emit("admin-join-user", { pcName });
  };
  const selectedPc = userList.find((user) => user.pcName === selectedUser);
  const onlineCount = userList.filter((user) => user.socketId).length;
  const screenshotCount = posts.filter((post) => post.type).length;

  return (
    <div className="screenshotsSection">
      <aside className="users-list">
        <div className="monitor-brand">
          <span className="monitor-brand-icon">
            <DesktopOutlined />
          </span>
          <div>
            <h2>Activity monitor</h2>
            <p>Connected workstations</p>
          </div>
        </div>
        <div className="fleet-summary">
          <span>
            <CheckCircleFilled /> {onlineCount} online
          </span>
          <span>{userList.length} total</span>
        </div>
        <div className="user-status-list">
          {!userList.length && (
            <div className="no-pcs">
              <DisconnectOutlined />
              <span>No PCs connected yet</span>
            </div>
          )}
          {userList.map((user) => (
            <button
              type="button"
              key={user.pcName}
              className={`user-status-item ${selectedUser === user.pcName ? "selected" : ""}`}
              onClick={() => selectUser(user.pcName)}
            >
              <span className="pc-avatar">
                <LaptopOutlined />
              </span>
              <span className="pc-copy">
                <strong>{user.pcName}</strong>
                <small>
                  <i className={user.socketId ? "online" : ""} />
                  {user.socketId ? "Online now" : "Offline"}
                </small>
              </span>
              <span className="pc-chevron">›</span>
            </button>
          ))}
        </div>
      </aside>
      {!selectedUser ? (
        <main className="screenshots-empty">
          <div className="empty-illustration">
            <EyeOutlined />
          </div>
          <h1>Select a workstation</h1>
          <p>
            Choose a PC from the sidebar to review screenshots and recent
            activity.
          </p>
        </main>
      ) : (
        <main className="screenshots-sub-main">
          <header className="screenshots-heading">
            <div className="heading-copy">
              <div className="heading-eyebrow">WORKSTATION ACTIVITY</div>
              <h1>{selectedUser}</h1>
              <span
                className={`presence-pill ${selectedPc?.socketId ? "is-online" : ""}`}
              >
                <i />
                {selectedPc?.socketId ? "Live now" : "Offline - saved history"}
              </span>
            </div>
            <div className="monitor-actions">
              {!audioEnabled && (
                <button
                  className="enable-sound-button"
                  onClick={() => {
                    try {
                      playBeep();
                      setAudioEnabled(true);
                    } catch (err) {
                      console.warn(err);
                    }
                  }}
                >
                  <BellOutlined /> Enable alerts
                </button>
              )}
              <button
                type="button"
                className={
                  selectedPc?.captureEnabled === false
                    ? "start-capture-btn"
                    : "close-window-btn"
                }
                disabled={!selectedPc?.socketId}
                onClick={() =>
                  socket.current?.emit("set-capture-state", {
                    pcName: selectedUser,
                    enabled: selectedPc?.captureEnabled === false,
                  })
                }
              >
                {selectedPc?.captureEnabled === false ? (
                  <>
                    <CheckCircleFilled /> Start capture
                  </>
                ) : (
                  <>
                    <CloseOutlined /> Stop capture
                  </>
                )}
              </button>
            </div>
          </header>
          <section className="activity-stats">
            <div>
              <span className="stat-icon screen">
                <DesktopOutlined />
              </span>
              <p>
                <strong>{screenshotCount}</strong>
                <small>Screenshots loaded</small>
              </p>
            </div>
            <div>
              <span className="stat-icon keys">
                <FieldTimeOutlined />
              </span>
              <p>
                <strong>{posts.length - screenshotCount}</strong>
                <small>Activity events</small>
              </p>
            </div>
            <div>
              <span className="stat-icon time">
                <ClockCircleOutlined />
              </span>
              <p>
                <strong>{posts.length ? "Recent" : "--"}</strong>
                <small>Latest activity</small>
              </p>
            </div>
          </section>
          <div className="feed-heading">
            <div>
              <h2>Activity feed</h2>
              <p>Newest events appear first</p>
            </div>
            <span>{posts.length} loaded</span>
          </div>
          <div className="screenshots-section">
            {posts.map((post, index) => (
              <article
                key={post._id || `${post.image_path}-${index}`}
                className="screenshot-item"
              >
                {post.type ? (
                  <>
                    <img
                      src={`${API_BASE_URL}/screenshots/${post.image_path}`}
                      className="screenshot-img"
                      alt={`Capture from ${selectedUser}`}
                    />
                    <div className="capture-meta">
                      <span>
                        <DesktopOutlined />{" "}
                        {post.display_count > 1
                          ? `Display ${post.display_index} of ${post.display_count}`
                          : "Screenshot"}
                      </span>
                      <time>
                        {post.created_date
                          ? new Date(post.created_date).toLocaleString()
                          : "Live capture"}
                      </time>
                    </div>
                  </>
                ) : (
                  <div className="screenshot-key">
                    <FieldTimeOutlined className="activity-icon" />
                    <span>{post.image_path}</span>
                    {post.created_date && (
                      <small>
                        {new Date(post.created_date).toLocaleString()}
                      </small>
                    )}
                  </div>
                )}
              </article>
            ))}
            {!loading && !posts.length && (
              <div className="feed-empty">
                <ClockCircleOutlined />
                <h3>No activity yet</h3>
                <p>New screenshots and keyboard activity will appear here.</p>
              </div>
            )}
          </div>
          {loading && (
            <div className="screenshot-loading-section">
              <div className="screenshot-loading-circle" />
              <div>Loading activity...</div>
            </div>
          )}
          <div ref={loader} />
          {!hasMore && posts.length > 0 && (
            <p className="feed-end">
              You have reached the end of the activity history
            </p>
          )}
        </main>
      )}
    </div>
  );
};

export default Screenshots;
